import { Accordion as AccordionPrimitive } from "radix-ui";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { ImageWithQuantity } from "~/components/image-with-quantity/image-with-quantity";
import { cn } from "~/components/ui/cn";
import { ArrowDownIcon } from "~/components/ui/icons/arrow-down";
import { RefundChooseIcon } from "~/components/ui/icons/refund-choose";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type { OrderQuery } from "~/graphql/graphql-new.gen";
import { itemReturnReasons, itemReturnSubReasons } from "~/stores/order";
import { transformPrice } from "~/utils/currency";

export function OrderItem({
  item,
}: {
  item: OrderQuery["order"]["items"][number];
}) {
  const { t, i18n } = useTranslation();

  const itemReturnReason = itemReturnReasons.find(
    (reason) => reason.id === item.returnReasonCode,
  );
  const itemReturnSubReason = itemReturnSubReasons.find(
    (reason) => reason.id === item.returnSubReasonCode,
  );

  return (
    <div className="flex flex-row items-start space-x-4">
      <ImageWithQuantity
        imageUrl={item.imageUrl || ""}
        quantity={1}
        className={cn("size-20", {
          "opacity-25": item.isCancelled,
        })}
      />
      <div className="flex min-h-20 flex-1 flex-col justify-center space-y-1">
        <p>
          <span
            className={cn("text-sm font-semibold", {
              "text-gray-900": !item.isCancelled,
              "text-gray-500": item.isCancelled,
            })}
          >
            {item.name}
          </span>{" "}
          {item.isOffered && (
            <span className="mr-1 text-sm lowercase text-orange-600">
              ({t("order.is_offered")})
            </span>
          )}
          <span
            className={`text-sm ${
              item.isCancelled ? "text-gray-300" : "text-gray-500"
            }`}
          >
            (
            {transformPrice(
              item.priceSold.valueWithVat,
              i18n.language === "fr" ? "fr" : "en",
              item.priceSold.currency,
            )}
            )
          </span>
        </p>
        {item.size ? (
          <p
            className={cn(`text-xs`, {
              "text-gray-700": !item.isCancelled,
              "text-gray-500": item.isCancelled,
            })}
          >
            {item.size}
          </p>
        ) : null}
        {item.sku ? (
          <p
            className={cn(`text-xs`, {
              "text-gray-700": !item.isCancelled,
              "text-gray-500": item.isCancelled,
            })}
          >
            <span className="uppercase">{t("order.sku")}</span> {item.sku}
          </p>
        ) : null}
        {item.gtin ? (
          <p
            className={cn(`text-xs`, {
              "text-gray-700": !item.isCancelled,
              "text-gray-500": item.isCancelled,
            })}
          >
            <span className="uppercase">{t("order.gtin")}</span> {item.gtin}
          </p>
        ) : null}
        {item.newDigitalCouponCode || item.digitalCouponCode ? (
          <div className="flex w-full items-start justify-between space-x-4">
            <p className="break-all text-xs text-[#767A7C]">
              <span className="uppercase">
                {item.newDigitalCouponCode ?? item.digitalCouponCode}
              </span>
            </p>
          </div>
        ) : null}
        {!item.isCancelled &&
        (item.isReturnGenerated ||
          item.refundSummary.refundedAmountInPercentage > 0) ? (
          <AccordionPrimitive.Root
            type="single"
            disabled={
              !item.refundSummary.refundedAmountInPercentageCoveredByChoose ||
              !item.refundSummary.refundedAmountInPercentageCoveredBySupplier
            }
            collapsible
            className="space-x-2 border-l border-gray-100 pl-2 text-xs text-gray-700"
          >
            <AccordionPrimitive.Item value="refund-details">
              <AccordionPrimitive.Header>
                <AccordionPrimitive.Trigger className="group flex items-center space-x-1">
                  {item.isReturnGenerated ? (
                    <Fragment>
                      {itemReturnReason ? (
                        <span className="font-bold">
                          <span>
                            {t("order.returned_with_reason")}{" "}
                            {itemReturnReason.label}
                          </span>
                          {itemReturnSubReason ? (
                            <span> - {itemReturnSubReason.label}</span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="font-bold">{t("order.returned")}</span>
                      )}
                    </Fragment>
                  ) : null}
                  {item.isReturnGenerated &&
                  item.refundSummary.refundedAmountInPercentage > 0 ? (
                    <span className="text-gray-300">•</span>
                  ) : null}
                  {item.refundSummary.refundedAmountInPercentage > 0 ? (
                    <span className="text-xs font-semibold text-orange-600">
                      {item.refundSummary.isTotallyRefunded
                        ? t("order.refunded")
                        : t("order.x_refunded", {
                            count:
                              item.refundSummary.refundedAmountInPercentage,
                          })}
                    </span>
                  ) : null}
                  {item.refundSummary
                    .refundedAmountInPercentageCoveredByChoose !== 0 &&
                  item.refundSummary
                    .refundedAmountInPercentageCoveredBySupplier === 0 ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <RefundChooseIcon className="text-orange-600" />
                      </TooltipTrigger>
                      <TooltipContent sideOffset={8}>
                        <div className="px-1.5 py-3 text-xs font-semibold">
                          <p className="text-center">
                            {t("order.refund.payable_by_choose")}
                          </p>
                        </div>
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                  <ArrowDownIcon className="size-4 text-orange-600 transition-transform group-data-[disabled]:hidden group-data-[state=open]:rotate-180" />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionPrimitive.Content>
                <div className="ml-2 mt-1 space-y-1 text-xs font-normal text-gray-900">
                  {item.refundSummary
                    .refundedAmountInPercentageCoveredByChoose ? (
                    <p>
                      {item.refundSummary.isTotallyRefunded ||
                      !item.refundSummary
                        .refundedAmountInPercentageCoveredBySupplier
                        ? t("order.refund.refunded_by_choose")
                        : t("order.refund.x_refunded_by_choose", {
                            amount:
                              item.refundSummary
                                .refundedAmountInPercentageCoveredByChoose,
                          })}
                    </p>
                  ) : null}
                  {item.refundSummary
                    .refundedAmountInPercentageCoveredBySupplier ? (
                    <p>
                      {item.refundSummary.isTotallyRefunded ||
                      !item.refundSummary
                        .refundedAmountInPercentageCoveredByChoose
                        ? t("order.refund.refunded_by_supplier")
                        : t("order.refund.x_refunded_by_supplier", {
                            amount:
                              item.refundSummary
                                .refundedAmountInPercentageCoveredBySupplier,
                          })}
                    </p>
                  ) : null}
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          </AccordionPrimitive.Root>
        ) : null}
        {item.isCancelled ? (
          <div className="flex items-center space-x-2">
            <div className="h-4 w-0.5 shrink-0 bg-gray-100"></div>
            <p className="text-xs font-semibold text-orange-600">
              {t("order.cancelled")}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
